import {
  DeleteRestaurant,
  GetRestaurantDropdownList,
  GetRestaurantList,
  PutRestaurant,
  QueryParamsGetList,
  QueryRestaurantDropdownList,
  RestaurantDetail,
  RestaurantSchema,
  UpdateRestaurant,
  UpdateRestaurantPublishStatus,
  restaurantOrderByType,
} from '@/types/index';
import {
  RestaurantCreate,
  RestaurantDelete,
  RestaurantDetailGet,
  RestaurantDropdownListGet,
  RestaurantListGet,
  RestaurantUpdate,
  RestaurantUpdatePublishStatus,
} from '@/services/http';
import { useContext, useEffect, useState } from 'react';

import { DEFAULT_ROW_PER_PAGE } from '@/src/app.constants';
import { RestaurantContext } from './initial.store';
import { STATE_ENUM } from '@/src/types/schema/enum/common.enum';
import { createRestaurantSchema } from '@/types/schema/createRestaurant';
import { updateRestaurantSchema } from '@/types/schema/updateRestaurant';

export function useRestaurantContext() {
  return useContext(RestaurantContext);
}

const RestaurantContextProvider = ({ children }: { children: React.ReactNode }) => {
  const [restaurantList, setRestaurantList] = useState<GetRestaurantList>({
    data: [],
    state: 'initial',
    status: {
      code: 0,
      message: '',
    },
  });

  const [restaurantDetail, setRestaurantDetail] = useState<RestaurantDetail>({
    data: undefined,
    state: 'initial',
    status: {
      code: 0,
      message: '',
    },
  });

  const [restaurantListForSchedule, setRestaurantListForSchedule] = useState<GetRestaurantList>({
    data: [],
    state: STATE_ENUM.INITIAL,
    status: {
      code: 0,
      message: '',
    },
  });

  const [restaurantListFallBackMeal, setRestaurantListFallBackMeal] = useState<GetRestaurantList>({
    data: [],
    state: STATE_ENUM.INITIAL,
    status: {
      code: 0,
      message: '',
    },
  });

  const [restaurantCreateData, setRestaurantCreateData] = useState<PutRestaurant>({
    data: undefined,
    state: STATE_ENUM.INITIAL,
    status: {
      code: 0,
      message: '',
    },
  });

  const [restaurantUpdateData, setRestaurantUpdateData] = useState<UpdateRestaurant>({
    data: undefined,
    state: STATE_ENUM.INITIAL,
    status: {
      code: 0,
      message: '',
    },
  });

  const [restaurantDeleteData, setRestaurantDeleteData] = useState<DeleteRestaurant>({
    data: undefined,
    state: STATE_ENUM.INITIAL,
    status: {
      code: 0,
      message: '',
    },
  });

  const [restaurantDropdownList, setRestaurantDropdownList] = useState<GetRestaurantDropdownList>({ state: STATE_ENUM.INITIAL });

  const [restaurantUpdatePublishStatusData, setRestaurantUpdatePublishStatusData] = useState<UpdateRestaurantPublishStatus>({
    data: undefined,
    state: STATE_ENUM.INITIAL,
    status: {
      code: 0,
      message: '',
    },
  });

  // action for restaurantList
  const getRestaurantList = (prefix: string, params?: QueryParamsGetList<restaurantOrderByType>) => {
    setRestaurantList({
      data: [],
      state: STATE_ENUM.LOADING,
      status: {
        code: 0,
        message: '',
      },
    });
    RestaurantListGet(prefix, params || ({ page: 0, itemPerPage: DEFAULT_ROW_PER_PAGE } as QueryParamsGetList<restaurantOrderByType>))
      .then((d) => {
        setRestaurantList({
          data: d.data,
          state: STATE_ENUM.READY,
          status: d.status,
          pagination: d.pagination,
        });
      })
      .catch((error) => {
        setRestaurantList({ data: [], error: error?.response, state: STATE_ENUM.ERROR, status: error?.response?.data?.status });
      });
  };

  // restaurant detail
  const getRestaurantDetail = (prefix: string, params?: string) => {
    setRestaurantDetail({
      data: undefined,
      state: STATE_ENUM.LOADING,
      status: {
        code: 0,
        message: '',
      },
    });
    RestaurantDetailGet(prefix, params || '')
      .then((d) => {
        setRestaurantDetail({
          data: d.data,
          state: STATE_ENUM.READY,
          status: d.status,
        });
      })
      .catch((error) => {
        setRestaurantDetail({ data: undefined, error: error?.response, state: STATE_ENUM.ERROR, status: error?.response?.data?.status });
      });
  };
  // NOTE: action to get restaurantList for dropdown on schedule page, get all restaurantList without pagination or sorting.
  const getRestaurantListForSchedule = (prefix: string) => {
    setRestaurantListForSchedule({ data: [], state: STATE_ENUM.LOADING });
    RestaurantListGet(prefix, { page: -1, isPublished: true } as QueryParamsGetList<restaurantOrderByType>)
      .then((d) => {
        setRestaurantListForSchedule({
          data: d.data,
          state: STATE_ENUM.READY,
          status: d.status,
          pagination: d.pagination,
        });
      })
      .catch((error) => {
        setRestaurantListForSchedule({ data: [], error: error?.response, state: STATE_ENUM.ERROR, status: error?.response?.data?.status });
      });
  };

  const getRestaurantListFallBackMeal = (prefix: string) => {
    setRestaurantListFallBackMeal({ data: [], state: STATE_ENUM.LOADING });
    RestaurantListGet(prefix, { page: -1 } as QueryParamsGetList<restaurantOrderByType>)
      .then((d) => {
        setRestaurantListFallBackMeal({
          data: d.data,
          state: STATE_ENUM.READY,
          status: d.status,
          pagination: d.pagination,
        });
      })
      .catch((error) => {
        setRestaurantListFallBackMeal({ data: [], error: error?.response, state: STATE_ENUM.ERROR, status: error?.response?.data?.status });
      });
  };

  // action for restaurant create
  const setRestaurantCreate = (prefix: string, payload: createRestaurantSchema) => {
    setRestaurantCreateData({
      data: undefined,
      state: 'loading',
      status: {
        code: 0,
        message: '',
      },
    });
    RestaurantCreate(prefix, payload)
      .then((data) => {
        setRestaurantCreateData({
          data: data.data,
          state: 'ready',
          status: data.status,
        });
      })
      .catch((error) => {
        setRestaurantCreateData({ data: undefined, error: error?.response, state: 'error', status: error?.response?.data?.status });
      });
  };

  // action for restaurant update
  const setRestaurantUpdate = (prefix: string, payload: updateRestaurantSchema, restaurantId: string) => {
    setRestaurantUpdateData({
      data: undefined,
      state: 'loading',
      status: {
        code: 0,
        message: '',
      },
    });
    RestaurantUpdate(prefix, restaurantId, payload)
      .then((data) => {
        setRestaurantUpdateData({
          data: data.data,
          state: 'ready',
          status: data.status,
        });
      })
      .catch((error) => {
        setRestaurantUpdateData({ data: undefined, error: error?.response, state: 'error', status: error?.response?.data?.status });
      });
  };

  // action for change restaurant list after update
  const getRestaurantIdUpdateList = (id: string, thName: string, enName: string) => {
    const startArray = restaurantList;
    const restaurantUpdateList = startArray.data.map((restaurant: RestaurantSchema) => {
      if (restaurant.id === id) {
        return {
          ...restaurant,
          thName,
          enName,
        };
      } else {
        return {
          ...restaurant,
        };
      }
    });
    setRestaurantList({
      data: restaurantUpdateList,
      state: 'ready',
      pagination: restaurantList.pagination,
    });
  };

  // action for restaurant delete
  const setRestaurantDelete = (prefix: string, restaurantId: string) => {
    setRestaurantDeleteData({
      data: undefined,
      state: 'loading',
      status: {
        code: 0,
        message: '',
      },
    });
    RestaurantDelete(prefix, restaurantId)
      .then((data) => {
        setRestaurantDeleteData({
          data: data.data,
          state: 'ready',
          status: data.status,
        });
      })
      .catch((error) => {
        setRestaurantDeleteData({ data: error?.response?.data?.data, error: error?.response, state: 'error', status: error?.response?.data?.status });
      });
  };

  // action for change restaurant list after delete
  const getRestaurantIdDeleteList = (id: string) => {
    const startArray = restaurantList;

    const restaurantDeleteList = startArray.data.filter((restaurant: RestaurantSchema) => {
      return restaurant.id !== id;
    });
    setRestaurantList({
      data: restaurantDeleteList,
      state: 'ready',
      pagination: {
        ...restaurantList.pagination,
        page: restaurantList.pagination?.page ?? 1,
        perPage: restaurantList.pagination?.perPage ?? 20,
        total: restaurantDeleteList.length,
      },
    });
    return restaurantDeleteList.length;
  };

  const getRestaurantDropdownList = (prefix: string, restaurantId: string, params: QueryRestaurantDropdownList) => {
    setRestaurantDropdownList({ state: 'loading' });
    RestaurantDropdownListGet(prefix, restaurantId, params)
      .then((res) => {
        setRestaurantDropdownList({
          data: res.data,
          state: 'ready',
          status: res.status,
        });
      })
      .catch((error) => {
        setRestaurantDropdownList({ data: undefined, error: error?.response, state: 'error', status: error?.response?.data?.status });
      });
  };

  const setRestaurantUpdatePublishStatus = (prefix: string, restaurantId: string, isPublished: boolean) => {
    setRestaurantUpdatePublishStatusData({
      data: undefined,
      state: 'loading',
      status: {
        code: 0,
        message: '',
      },
    });

    RestaurantUpdatePublishStatus(prefix, restaurantId, { isPublished })
      .then((data) => {
        setRestaurantUpdatePublishStatusData({
          data: data.data,
          state: 'ready',
          status: data.status,
        });
      })
      .catch((error) => {
        setRestaurantUpdatePublishStatusData({
          data: error?.response?.data?.data,
          error: error?.response,
          state: 'error',
          status: error?.response?.data?.status,
        });
      });
  };

  const restaurantStore = {
    restaurantList,
    setRestaurantList: getRestaurantList,
    setRestaurantListData: setRestaurantList,
    restaurantListForSchedule,
    setRestaurantListForSchedule: getRestaurantListForSchedule,

    restaurantListFallBackMeal,
    setRestaurantListFallBackMeal: getRestaurantListFallBackMeal,

    restaurantCreateData,
    setRestaurantCreate,
    setRestaurantCreateData,

    restaurantUpdateData,
    setRestaurantUpdate,
    setRestaurantUpdateData,

    getRestaurantIdUpdateList,

    restaurantDeleteData,
    setRestaurantDelete,
    setRestaurantDeleteData,

    getRestaurantIdDeleteList,

    restaurantDropdownList,
    setRestaurantDropdownList: getRestaurantDropdownList,
    setRestaurantDropdownListData: setRestaurantDropdownList,

    restaurantDetail,
    setRestaurantDetail: getRestaurantDetail,
    restaurantDetailForDetail: restaurantDetail,
    setRestaurantDetailData: setRestaurantDetail,
    setRestaurantDetailForDetail: (prefix: string) => {},

    restaurantUpdatePublishStatusData,
    setRestaurantUpdatePublishStatus,
    setRestaurantUpdatePublishStatusData,
  };

  useEffect(() => {
    return () => {
      setRestaurantDeleteData({ state: STATE_ENUM.INITIAL, data: undefined });
      setRestaurantUpdateData({ state: STATE_ENUM.INITIAL, data: undefined });
      setRestaurantCreateData({ state: STATE_ENUM.INITIAL, data: undefined });
      setRestaurantList({ state: STATE_ENUM.INITIAL, data: [] });
      setRestaurantDetail({ state: STATE_ENUM.INITIAL, data: undefined });
    };
  }, []);

  return <RestaurantContext.Provider value={restaurantStore}>{children}</RestaurantContext.Provider>;
};

export { RestaurantContextProvider };
